import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import axios from 'axios';
import { eoiGuestSubmitRequestSchema } from '@court-sync/shared';
import type { EoiGuestSubmitRequest } from '@court-sync/shared';
import { eoiService } from '../../../services/eoi';
import { usePublicBookingStore } from '../../../store/publicBooking';
import { OrgSignInLink } from '@/components/public/org/OrgSignInLink';
import { appendPublicBookingWizardContextQueries, publicBookingStepPath } from './bookingPaths';
import { buildAcademyPath } from '../../../lib/academyPaths';
import { Button, Field, FieldError, FieldLabel, Input, Textarea, PhoneField } from '@/components/ui';
import { HeartHandshake } from 'lucide-react';

type GuestEoiFormValues = EoiGuestSubmitRequest;

export interface PublicBookingGuestEOIFormProps {
  academySlug: string;
  lessonId: string;
  academyId: string;
  lessonName?: string;
  invitationToken?: string | null;
  manualInviteToken?: string | null;
}

export function PublicBookingGuestEOIForm({
  academySlug,
  lessonId,
  academyId,
  lessonName,
  invitationToken,
  manualInviteToken,
}: PublicBookingGuestEOIFormProps) {
  const [submitted, setSubmitted] = useState(false);
  const setUserType = usePublicBookingStore((s) => s.setUserType);

  const {
    register,
    control,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<GuestEoiFormValues>({
    resolver: zodResolver(eoiGuestSubmitRequestSchema),
    defaultValues: {
      academyId,
      lessonId,
      experienceLevel: 'beginner',
      goals: '',
      availability: '',
      message: '',
      guardianFirstName: '',
      guardianLastName: '',
      guardianPhone: '',
      guestContactEmail: '',
      studentFirstName: '',
      studentLastName: '',
    },
  });

  useEffect(() => {
    setValue('academyId', academyId);
    setValue('lessonId', lessonId);
  }, [academyId, lessonId, setValue]);

  const onSubmit = async (data: GuestEoiFormValues) => {
    try {
      const res = await eoiService.submitGuestEOI({
        ...data,
        academyId,
        lessonId,
      });
      if (res.success) {
        setSubmitted(true);
        toast.success('Your expression of interest has been submitted.');
      } else {
        toast.error(res.error || 'Submission failed');
      }
    } catch (e: unknown) {
      const msg =
        axios.isAxiosError(e) && e.response?.data && typeof e.response.data === 'object' && 'error' in e.response.data
          ? String((e.response.data as { error?: string }).error)
          : e instanceof Error
            ? e.message
            : 'Submission failed';
      toast.error(msg);
    }
  };

  const loginReturn = appendPublicBookingWizardContextQueries(
    publicBookingStepPath(academySlug, lessonId, 'register'),
    { invitationToken, manualInviteToken }
  );
  const registerHref = appendPublicBookingWizardContextQueries(
    publicBookingStepPath(academySlug, lessonId, 'signup'),
    { invitationToken, manualInviteToken }
  );

  if (submitted) {
    return (
      <div className="space-y-6">
        <div className="max-w-lg mx-auto px-0 py-6 text-center space-y-4">
          <HeartHandshake className="w-14 h-14 mx-auto text-indigo-600" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Thank you</h2>
          <p className="text-gray-600 dark:text-gray-400">
            Your expression of interest has been submitted. The academy will review your application and contact you
            about next steps. You will receive a confirmation email shortly.
          </p>
          <Button type="button" variant="outline" onClick={() => window.location.assign(buildAcademyPath({ slug: academySlug }, ''))}>
            Back to academy
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-2xl font-bold text-foreground mb-2">Expression of interest</h3>
        <p className="text-muted-foreground max-w-md mx-auto">
          {lessonName ? (
            <>
              <span className="font-medium text-foreground">{lessonName}</span>
              {' — '}
            </>
          ) : null}
          This program uses invitations. Share your details and the academy will be in touch. No account is required to
          submit.
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 max-w-md mx-auto">
        <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-4">
          <p className="text-sm font-medium text-foreground">Parent / guardian</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field data-invalid={!!errors.guardianFirstName}>
              <FieldLabel htmlFor="guest-guardian-first">First name *</FieldLabel>
              <Input id="guest-guardian-first" autoComplete="given-name" {...register('guardianFirstName')} />
              <FieldError>{errors.guardianFirstName?.message}</FieldError>
            </Field>
            <Field data-invalid={!!errors.guardianLastName}>
              <FieldLabel htmlFor="guest-guardian-last">Last name *</FieldLabel>
              <Input id="guest-guardian-last" autoComplete="family-name" {...register('guardianLastName')} />
              <FieldError>{errors.guardianLastName?.message}</FieldError>
            </Field>
          </div>
          <Field data-invalid={!!errors.guestContactEmail}>
            <FieldLabel htmlFor="guest-email">Email *</FieldLabel>
            <Input
              id="guest-email"
              type="email"
              autoComplete="email"
              placeholder="you@example.com"
              {...register('guestContactEmail')}
            />
            <FieldError>{errors.guestContactEmail?.message}</FieldError>
          </Field>
          <PhoneField
            control={control}
            name="guardianPhone"
            label="Phone (optional)"
            id="guest-phone"
            purpose="guest_inquiry"
            placeholder="Phone number"
          />
        </div>

        <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-4">
          <p className="text-sm font-medium text-foreground">Player</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field data-invalid={!!errors.studentFirstName}>
              <FieldLabel htmlFor="guest-student-first">First name *</FieldLabel>
              <Input id="guest-student-first" {...register('studentFirstName')} />
              <FieldError>{errors.studentFirstName?.message}</FieldError>
            </Field>
            <Field data-invalid={!!errors.studentLastName}>
              <FieldLabel htmlFor="guest-student-last">Last name *</FieldLabel>
              <Input id="guest-student-last" {...register('studentLastName')} />
              <FieldError>{errors.studentLastName?.message}</FieldError>
            </Field>
          </div>
        </div>

        <Field data-invalid={!!errors.experienceLevel}>
          <FieldLabel htmlFor="guest-exp">Experience level *</FieldLabel>
          <select id="guest-exp" className="input w-full" {...register('experienceLevel')}>
            <option value="beginner">Beginner</option>
            <option value="intermediate">Intermediate</option>
            <option value="advanced">Advanced</option>
          </select>
          <FieldError>{errors.experienceLevel?.message}</FieldError>
        </Field>

        <Field>
          <FieldLabel htmlFor="guest-goals">Goals</FieldLabel>
          <Textarea id="guest-goals" rows={3} placeholder="What would you like to achieve?" {...register('goals')} />
        </Field>

        <Field>
          <FieldLabel htmlFor="guest-avail">Availability</FieldLabel>
          <Textarea
            id="guest-avail"
            rows={2}
            placeholder="Preferred days or times"
            {...register('availability')}
          />
        </Field>

        <Field>
          <FieldLabel htmlFor="guest-msg">Message</FieldLabel>
          <Textarea id="guest-msg" rows={3} placeholder="Anything else we should know" {...register('message')} />
        </Field>

        <Button type="submit" disabled={isSubmitting} className="w-full">
          {isSubmitting ? 'Submitting…' : 'Submit expression of interest'}
        </Button>
      </form>

      <div className="text-center text-sm text-muted-foreground border-t border-border pt-6 max-w-md mx-auto space-y-2">
        <p>Already use CourtSync with this academy?</p>
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 justify-center items-center">
          <OrgSignInLink returnUrl={loginReturn} className="text-primary hover:underline">
            Sign in
          </OrgSignInLink>
          <span className="hidden sm:inline text-border">·</span>
          <Link
            to={registerHref}
            onClick={() => setUserType('new')}
            className="text-primary font-medium hover:underline"
          >
            Create an account
          </Link>
        </div>
        <p className="text-xs pt-2">
          Signing in lets you manage bookings and profiles; you can still submit interest above without an account.
        </p>
      </div>
    </div>
  );
}
